import { createClient } from "@supabase/supabase-js";
import { Bank, BankBranch, ImportLog, AffiliateCampaign, RevenueSetting } from "../types";

// Read configuration strictly from Vite build environment variables
const ENV_URL = (import.meta as any).env.VITE_SUPABASE_URL || "";
const ENV_KEY = (import.meta as any).env.VITE_SUPABASE_ANON_KEY || "";

// Hard security remediation: Purge any user-configured legacy credentials stored in localStorage
try {
  localStorage.removeItem("finsahaj_sb_url");
  localStorage.removeItem("finsahaj_sb_key");
} catch (e) {
  // Silent catch in case localStorage is disabled or barred in special frames
}

// Initialize real Supabase client with safe checks and trim trailing/accidental whitespaces
let tempSupabase = null;
if (ENV_URL && ENV_KEY) {
  try {
    const cleanUrl = ENV_URL.trim().replace(/\s/g, "");
    const cleanKey = ENV_KEY.trim().replace(/\s/g, "");

    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      tempSupabase = createClient(cleanUrl, cleanKey);
    } else {
      console.warn(
        "Invalid Supabase Endpoint URI format loaded from environment variables. Must start with http:// or https://.",
      );
    }
  } catch (err) {
    console.error("Failed to initialize Supabase JS client:", err);
  }
}

export const supabase = tempSupabase;

export const isSupabaseConfigured = () => {
  return !!supabase;
};

// Simple active in-memory cache to avoid redundant central database requests
interface CacheData {
  banks: Bank[] | null;
  popularBanks: Record<number, Bank[]>;
  banksByState: Record<string, Bank[]>;
  stateBranchCounts: Record<string, number>;
  bankBySlug: Record<string, Bank | undefined>;
  branchByIfsc: Record<string, BankBranch | undefined>;
  branchesByBankAndState: Record<string, BankBranch[]>;
  branchesByBankId: Record<string, { data: BankBranch[]; count: number }>;
  searchBranches: Record<
    string,
    { data: (BankBranch & { bankName: string })[]; timestamp: number }
  >;
  totalBranchCount?: number;
  // Sprint-02 Approved Performance Cache Layers
  cityBranchCounts: Record<string, number>;
  branchesByCity: Record<string, { data: (BankBranch & { bankName: string })[]; count: number }>;
  banksInCity: Record<string, Bank[]>;
  recentBranchesInCity: Record<string, (BankBranch & { bankName: string })[]>;
  bankUniqueStates: Record<string, string[]>;
}

const dbCache: CacheData = {
  banks: null,
  popularBanks: {},
  banksByState: {},
  stateBranchCounts: {},
  bankBySlug: {},
  branchByIfsc: {},
  branchesByBankAndState: {},
  branchesByBankId: {},
  searchBranches: {},
  // Sprint-02 Initializations
  cityBranchCounts: {},
  branchesByCity: {},
  banksInCity: {},
  recentBranchesInCity: {},
  bankUniqueStates: {},
};

// ==========================================
// UNIFIED REAL DATABASE SERVICE LAYER Only
// ==========================================
export const dbService = {
  isConfigured() {
    return isSupabaseConfigured();
  },

  async getBanks(): Promise<Bank[]> {
    if (dbCache.banks) {
      return dbCache.banks;
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("banks")
          .select("id, name, slug, logo_url")
          .order("name", { ascending: true });
        if (!error && data) {
          dbCache.banks = data as Bank[];
          return dbCache.banks;
        }
        console.error("Supabase banks query failed:", error);
      } catch (e) {
        console.error("Database connection error:", e);
      }
    }
    return [];
  },

  async getPopularBanks(limit = 6): Promise<Bank[]> {
    if (dbCache.popularBanks[limit]) {
      return dbCache.popularBanks[limit];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("banks")
          .select("id, name, slug, logo_url")
          .order("name", { ascending: true })
          .limit(limit);
        if (!error && data) {
          dbCache.popularBanks[limit] = data as Bank[];
          return dbCache.popularBanks[limit];
        }
      } catch (e) {
        console.error("Popular banks error:", e);
      }
    }
    return [];
  },

  async getBankBySlug(slug: string): Promise<Bank | undefined> {
    const key = slug.toLowerCase().trim();
    if (dbCache.bankBySlug[key] !== undefined) {
      return dbCache.bankBySlug[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("banks")
          .select("id, name, slug, logo_url")
          .eq("slug", slug.toLowerCase())
          .maybeSingle();
        if (!error && data) {
          dbCache.bankBySlug[key] = data as Bank;
          return dbCache.bankBySlug[key];
        }
      } catch (e) {
        console.error("Bank by slug error:", e);
      }
    }
    return undefined;
  },

  async getBranchByIfsc(ifsc: string): Promise<BankBranch | undefined> {
    const key = ifsc.toUpperCase().trim();
    if (dbCache.branchByIfsc[key] !== undefined) {
      return dbCache.branchByIfsc[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("bank_branches")
          .select(
            "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, std_code, phone, slug, banks(id, name, slug, logo_url)",
          )
          .eq("ifsc", ifsc.toUpperCase())
          .maybeSingle();
        if (!error && data) {
          const br = data as any;
          const result = {
            ...br,
            banks: br.banks || undefined,
          } as BankBranch;
          dbCache.branchByIfsc[key] = result;
          return result;
        }
      } catch (e) {
        console.error("Branch by IFSC query error:", e);
      }
    }
    return undefined;
  },

  async getBranchesByBankId(
    bankId: string,
    limit = 50,
    offset = 0,
  ): Promise<{ data: BankBranch[]; count: number }> {
    const key = `${bankId}_${limit}_${offset}`.trim().toUpperCase();
    if (dbCache.branchesByBankId[key] !== undefined) {
      return dbCache.branchesByBankId[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const fromIdx = offset;
        const toIdx = offset + limit - 1;
        const { data, count, error } = await supabase
          .from("bank_branches")
          .select(
            "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, slug",
            { count: "exact" },
          )
          .eq("bank_id", bankId)
          .order("branch_name", { ascending: true })
          .range(fromIdx, toIdx);
        if (!error && data) {
          const result = { data: data as BankBranch[], count: count || 0 };
          dbCache.branchesByBankId[key] = result;
          return result;
        }
      } catch (e) {
        console.error("Branches by bank query error:", e);
      }
    }
    return { data: [], count: 0 };
  },

  async getBranchesByBankAndState(
    bankId: string,
    stateName: string,
  ): Promise<BankBranch[]> {
    const standardState = stateName.replace(/-/g, " ").toUpperCase();
    const key = `${bankId}_${standardState} ${stateName}`.trim().toUpperCase();
    if (dbCache.branchesByBankAndState[key] !== undefined) {
      return dbCache.branchesByBankAndState[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("bank_branches")
          .select(
            "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, slug",
          )
          .eq("bank_id", bankId)
          .eq("state", standardState)
          .limit(1000);
        if (!error && data) {
          dbCache.branchesByBankAndState[key] = data as BankBranch[];
          return dbCache.branchesByBankAndState[key];
        }
      } catch (e) {
        console.error("Branches by bank & state error:", e);
      }
    }
    return [];
  },

  async getStateBranchCount(stateName: string): Promise<number> {
    const key = stateName.trim().toUpperCase();
    if (dbCache.stateBranchCounts[key] !== undefined) {
      return dbCache.stateBranchCounts[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const standardState = stateName.replace(/-/g, " ").toUpperCase();
        const { count, error } = await supabase
          .from("bank_branches")
          .select("id", { count: "exact", head: true })
          .eq("state", standardState);
        if (!error && count !== null) {
          dbCache.stateBranchCounts[key] = count;
          return count;
        }
      } catch (e) {
        console.error("Error fetching state branch count:", e);
      }
    }
    return 0;
  },

  async getTotalBranchCount(): Promise<number> {
    const key = "__TOTAL__";
    if (dbCache.stateBranchCounts[key] !== undefined) {
      return dbCache.stateBranchCounts[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { count, error } = await supabase
          .from("bank_branches")
          .select("id", { count: "exact", head: true });
        if (!error && count !== null) {
          dbCache.stateBranchCounts[key] = count;
          return count;
        }
      } catch (e) {
        console.error("Error fetching total branch count:", e);
      }
    }
    // Never return stale branch counts
    return dbCache.totalBranchCount ?? 0;
  },

  async getUniqueCitiesForState(stateName: string): Promise<string[]> {
    if (!stateName) return [];

    const cacheKey = `CITIES_COL_${stateName.trim().toUpperCase()}`;

    if ((dbCache as any)[cacheKey] !== undefined) {
      return (dbCache as any)[cacheKey] as string[];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const standardState = stateName.replace(/-/g, " ").toUpperCase();

        const { data, error } = await supabase.rpc("get_cities_by_state", {
          p_state: standardState,
        });

        if (!error && data) {
          const cities = (data as any[])
            .map((row: any) =>
              String(row.city || "")
                .trim()
                .toUpperCase(),
            )
            .filter(Boolean)
            .sort();

          const uniqueCities = Array.from(new Set(cities));

          (dbCache as any)[cacheKey] = uniqueCities;

          return uniqueCities;
        }

        console.error("get_cities_by_state RPC error:", error);
      } catch (e) {
        console.error("Error fetching cities via RPC:", e);
      }
    }

    return [];
  },

  async getBankUniqueStates(bankId: string): Promise<string[]> {
    const cacheKey = bankId.trim().toUpperCase();
    if (dbCache.bankUniqueStates[cacheKey] !== undefined) {
      return dbCache.bankUniqueStates[cacheKey];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("bank_branches")
          .select("state")
          .eq("bank_id", bankId);
        if (!error && data) {
          const states = Array.from(
            new Set(data.map((item: any) => item.state.toUpperCase())),
          ).sort();
          dbCache.bankUniqueStates[cacheKey] = states as string[];
          return dbCache.bankUniqueStates[cacheKey];
        }
      } catch (e) {
        console.error("Error loading bank states:", e);
      }
    }
    return [];
  },

  async getBranchesByState(
    stateName: string,
    page = 1,
    pageSize = 20,
  ): Promise<{ data: (BankBranch & { bankName: string })[]; count: number }> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const standardState = stateName.replace(/-/g, " ").toUpperCase();
        const fromIdx = (page - 1) * pageSize;
        const toIdx = fromIdx + pageSize - 1;

        // Check if we already have the count cached for this state to optimize the query speed
        const key = standardState.trim().toUpperCase();
        const cachedCount = dbCache.stateBranchCounts[key];

        const { data, count, error } = await supabase
          .from("bank_branches")
          .select(
            "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, phone, std_code, slug, banks(id, name, slug, logo_url)",
            cachedCount !== undefined ? {} : { count: "exact" },
          )
          .eq("state", standardState)
          .order("branch_name", { ascending: true })
          .range(fromIdx, toIdx);

        if (!error && data) {
          const list = (data as any[]).map((br) => ({
            ...br,
            bankName: br.banks ? br.banks.name : "Unknown Bank",
          }));
          const finalCount =
            cachedCount !== undefined ? cachedCount : count || 0;
          if (cachedCount === undefined && count !== null) {
            dbCache.stateBranchCounts[key] = count;
          }
          return { data: list, count: finalCount };
        }
        console.error("getBranchesByState error:", error);
      } catch (e) {
        console.error("getBranchesByState catch error:", e);
      }
    }
    return { data: [], count: 0 };
  },

  async getBanksByState(stateName: string): Promise<Bank[]> {
    const key = stateName.trim().toUpperCase();

    if (dbCache.banksByState[key]) {
      return dbCache.banksByState[key];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const standardState = stateName.replace(/-/g, " ").toUpperCase();

        const { data, error } = await supabase.rpc("get_banks_by_state", {
          p_state: standardState,
        });

        if (!error && data) {
          const result: Bank[] = (data as any[])
            .map((row: any) => ({
              id: row.id,
              name: row.name,
              slug: row.slug,
              logo_url: row.logo_url,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          dbCache.banksByState[key] = result;

          return result;
        }

        console.error("get_banks_by_state RPC error:", error);
      } catch (e) {
        console.error("getBanksByState RPC catch error:", e);
      }
    }

    return [];
  },

  async getLatestImportLogs(limit?: number): Promise<ImportLog[]> {
    if (isSupabaseConfigured() && supabase) {
      try {
        let qBuilder = supabase
          .from("import_logs")
          .select(
            "id, filename, bank_name, records_imported, records_failed, new_records, updated_records, file_hash, uploaded_at",
          )
          .order("uploaded_at", { ascending: false });
        if (limit) {
          qBuilder = qBuilder.limit(limit);
        }
        const { data, error } = await qBuilder;
        if (!error && data) return data as ImportLog[];
      } catch (e) {
        console.error("Import logs query error:", e);
      }
    }
    return [];
  },

  async getPopularBanksBySlugs(slugs: string[]): Promise<Bank[]> {
    const cacheKey = slugs.join(",");
    if (
      (dbCache as any).popularBanksBySlugs &&
      (dbCache as any).popularBanksBySlugs[cacheKey] !== undefined
    ) {
      return (dbCache as any).popularBanksBySlugs[cacheKey];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("banks")
          .select("id, name, slug, logo_url")
          .in("slug", slugs);
        if (!error && data) {
          if (!(dbCache as any).popularBanksBySlugs) {
            (dbCache as any).popularBanksBySlugs = {};
          }
          (dbCache as any).popularBanksBySlugs[cacheKey] = data as Bank[];
          return data as Bank[];
        }
      } catch (e) {
        console.error("getPopularBanksBySlugs error:", e);
      }
    }
    return [];
  },

  async getTotalBankCount(): Promise<number> {
    const key = "__TOTAL_BANKS__";
    if (dbCache.stateBranchCounts[key] !== undefined) {
      return dbCache.stateBranchCounts[key];
    }
    if (isSupabaseConfigured() && supabase) {
      try {
        const { count, error } = await supabase
          .from("banks")
          .select("id", { count: "exact", head: true });
        if (!error && count !== null) {
          dbCache.stateBranchCounts[key] = count;
          return count;
        }
      } catch (e) {
        console.error("Error fetching total bank count:", e);
      }
    }
    return 0;
  },

  async getBranchesByBankAndQuery(
    bankId: string,
    query: string,
    limit = 50,
  ): Promise<BankBranch[]> {
    const cleanQuery = (query || "").trim();
    if (!cleanQuery) {
      return [];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        let data: any[] | null = null;
        let error: any = null;

        // Try PostgreSQL full-text search first
        try {
          const tokens = cleanQuery
            .split(/\s+/)
            .map((token) => token.replace(/[^a-zA-Z0-9]/g, "").trim())
            .filter(Boolean);

          if (tokens.length > 0) {
            const ftsTokens = tokens.map((t) => {
              const lowerToken = t.toLowerCase();
              if (lowerToken === "pnb") {
                return "(punjab:* | punb:*)";
              }
              if (lowerToken === "sbi") {
                return "((state:* & bank:* & india:*) | sbin:*)";
              }
              if (lowerToken === "bob") {
                return "((bank:* & of:* & baroda:*) | barb:*)";
              }
              if (lowerToken === "boi") {
                return "((bank:* & of:* & india:*) | bkid:*)";
              }
              if (lowerToken === "cbi") {
                return "((central:* & bank:* & india:*) | cbin:*)";
              }
              if (lowerToken === "bom") {
                return "((bank:* & of:* & maharashtra:*) | mahb:*)";
              }
              if (lowerToken === "uco") {
                return "((uco:* & bank:*) | ucba:*)";
              }
              return `${lowerToken}:*`;
            });

            const ftsQuery = ftsTokens.join(" & ");
            const { data: ftsData, error: ftsError } = await supabase
              .from("bank_branches")
              .select("id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, phone, std_code, slug")
              .eq("bank_id", bankId)
              .textSearch("search_text", ftsQuery)
              .limit(limit);

            data = ftsData;
            error = ftsError;
          }
        } catch (ftsErr) {
          console.warn("FTS inner failed inside getBranchesByBankAndQuery, falling back...", ftsErr);
        }

        // Fallback to traditional matching if full-text search was bypassed, failed, or returned no data (or an error)
        if (!data || data.length === 0 || error) {
          const { data: fallbackData, error: fallbackError } = await supabase
            .from("bank_branches")
            .select("id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, phone, std_code, slug")
            .eq("bank_id", bankId)
            .or(
              [
                `ifsc.ilike.%${cleanQuery}%`,
                `branch_name.ilike.%${cleanQuery}%`,
                `city1.ilike.%${cleanQuery}%`,
                `city2.ilike.%${cleanQuery}%`,
                `address.ilike.%${cleanQuery}%`,
                `state.ilike.%${cleanQuery}%`,
              ].join(","),
            )
            .limit(limit);

          data = fallbackData;
          error = fallbackError;
        }

        if (!error && data) {
          const result = data as BankBranch[];
          const q = cleanQuery.toUpperCase();
          result.sort((a, b) => {
            const branchA = (a.branch_name || "").toUpperCase().trim();
            const branchB = (b.branch_name || "").toUpperCase().trim();
            const ifscA = (a.ifsc || "").toUpperCase().trim();
            const ifscB = (b.ifsc || "").toUpperCase().trim();
            const cityA = (a.city1 || a.display_city || "").toUpperCase().trim();
            const cityB = (b.city1 || b.display_city || "").toUpperCase().trim();
            const stateA = (a.state || "").toUpperCase().trim();
            const stateB = (b.state || "").toUpperCase().trim();
            const addrA = (a.address || "").toUpperCase().trim();
            const addrB = (b.address || "").toUpperCase().trim();

            const score = (
              branch: string,
              ifsc: string,
              city: string,
              state: string,
              addr: string,
            ) => {
              // 1. Exact IFSC code matching
              if (ifsc === q) return 10000000;

              // 2. Exact Branch Name match
              if (branch === q) return 1000000;

              // 3. Starts With Check
              if (
                ifsc.startsWith(q) ||
                branch.startsWith(q) ||
                city.startsWith(q) ||
                state.startsWith(q)
              ) {
                let boost = 0;
                if (ifsc.startsWith(q)) boost += 50000;
                if (branch.startsWith(q)) boost += 30030;
                if (city.startsWith(q)) boost += 20000;
                if (state.startsWith(q)) boost += 10000;
                return 100000 + boost;
              }

              // 4. Contains Check
              const combined = `${branch} ${ifsc} ${city} ${state} ${addr}`.toUpperCase();
              if (combined.includes(q)) {
                let boost = 0;
                if (ifsc.includes(q)) boost += 500;
                if (branch.includes(q)) boost += 300;
                if (city.includes(q)) boost += 200;
                if (state.includes(q)) boost += 100;
                return 1000 + boost;
              }

              return 0;
            };

            return (
              score(branchB, ifscB, cityB, stateB, addrB) -
              score(branchA, ifscA, cityA, stateA, addrA)
            );
          });

          return result;
        }
      } catch (e) {
        console.error("getBranchesByBankAndQuery failed:", e);
      }
    }
    return [];
  },

  async searchBranches(
    query: string,
    stateName = "",
    cityName = "",
  ): Promise<(BankBranch & { bankName: string })[]> {
    const qKey = (query || "").trim().toLowerCase();
    const sKey = (stateName || "").trim().toLowerCase();
    const cKey = (cityName || "").trim().toLowerCase();
    const cacheKey = `q:${qKey}|s:${sKey}|c:${cKey}`;
    const now = Date.now();
    const cached = dbCache.searchBranches[cacheKey];

    // Serve cached search result immediately if it was fetched within last 10 minutes (600,000 ms)
    if (cached && now - cached.timestamp < 10 * 60 * 1000) {
      return cached.data;
    }

    const cleanQuery = (query || "").trim();
    // Search Length Protection: Require minimum 3 characters when no state and no city is selected
    if (
      !stateName &&
      !cityName &&
      cleanQuery.length > 0 &&
      cleanQuery.length < 3
    ) {
      return [];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        let data: any[] | null = null;
        let error: any = null;

        // Try PostgreSQL full-text search if query is non-empty
        if (cleanQuery) {
          try {
            const tokens = cleanQuery
              .split(/\s+/)
              .map((token) => token.replace(/[^a-zA-Z0-9]/g, "").trim())
              .filter(Boolean);

            if (tokens.length > 0) {
              const ftsTokens = tokens.map((t) => {
                const lowerToken = t.toLowerCase();
                if (lowerToken === "pnb") {
                  return "(punjab:* | punb:*)";
                }
                if (lowerToken === "sbi") {
                  return "((state:* & bank:* & india:*) | sbin:*)";
                }
                if (lowerToken === "bob") {
                  return "((bank:* & of:* & baroda:*) | barb:*)";
                }
                if (lowerToken === "boi") {
                  return "((bank:* & of:* & india:*) | bkid:*)";
                }
                if (lowerToken === "cbi") {
                  return "((central:* & bank:* & india:*) | cbin:*)";
                }
                if (lowerToken === "bom") {
                  return "((bank:* & of:* & maharashtra:*) | mahb:*)";
                }
                if (lowerToken === "uco") {
                  return "((uco:* & bank:*) | ucba:*)";
                }
                return `${lowerToken}:*`;
              });

              const ftsQuery = ftsTokens.join(" & ");
              let ftsBuilder = supabase
                .from("bank_branches")
                .select(
                  "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, std_code, phone, slug, banks(id, name, slug, logo_url)",
                )
                .textSearch("search_text", ftsQuery);

              if (stateName) {
                ftsBuilder = ftsBuilder.eq("state", stateName.toUpperCase());
              }
              if (cityName) {
                const city = cityName.toUpperCase();
                ftsBuilder = ftsBuilder.or(
                  `city1.eq.${city},city2.eq.${city},display_city.eq.${city}`,
                );
              }

              const response = await ftsBuilder.limit(50);
              data = response.data;
              error = response.error;
            }
          } catch (ftsErr) {
            console.warn(
              "Full-text search failed, falling back to basic matching:",
              ftsErr,
            );
          }
        }

        // Fallback to traditional matching if full-text search was bypassed, failed, or returned an error
        if (!data || error) {
          let fallbackBuilder = supabase
            .from("bank_branches")
            .select(
              "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, std_code, phone, slug, banks(id, name, slug, logo_url)",
            );

          if (stateName) {
            fallbackBuilder = fallbackBuilder.eq(
              "state",
              stateName.toUpperCase(),
            );
          }
          if (cityName) {
            const city = cityName.toUpperCase();
            fallbackBuilder = fallbackBuilder.or(
              `city1.eq.${city},city2.eq.${city},display_city.eq.${city}`,
            );
          }

          if (cleanQuery) {
            fallbackBuilder = fallbackBuilder.or(
              [
                `ifsc.ilike.%${cleanQuery}%`,
                `branch_name.ilike.%${cleanQuery}%`,
                `city1.ilike.%${cleanQuery}%`,
                `city2.ilike.%${cleanQuery}%`,
                `address.ilike.%${cleanQuery}%`,
                `state.ilike.%${cleanQuery}%`,
              ].join(","),
            );
          }

          const response = await fallbackBuilder.limit(50);
          data = response.data;
          error = response.error;
        }

        if (!error && data) {
          const result = (data as any[]).map((br) => ({
            ...br,
            bankName: br.banks ? br.banks.name : "Unknown Bank",
          }));

          // Apply Task 3 search ranking priority if a keyword is searched
          if (query.trim()) {
            const q = query.trim().toUpperCase();
            result.sort((a, b) => {
              const bankA = (a.bankName || a.bank_name || "")
                .toUpperCase()
                .trim();
              const bankB = (b.bankName || b.bank_name || "")
                .toUpperCase()
                .trim();
              const branchA = (a.branch_name || "").toUpperCase().trim();
              const branchB = (b.branch_name || "").toUpperCase().trim();
              const ifscA = (a.ifsc || "").toUpperCase().trim();
              const ifscB = (b.ifsc || "").toUpperCase().trim();
              const cityA = (a.city1 || a.display_city || "")
                .toUpperCase()
                .trim();
              const cityB = (b.city1 || b.display_city || "")
                .toUpperCase()
                .trim();
              const stateA = (a.state || "").toUpperCase().trim();
              const stateB = (b.state || "").toUpperCase().trim();
              const addrA = (a.address || "").toUpperCase().trim();
              const addrB = (b.address || "").toUpperCase().trim();

              const bankAndBranchA = `${bankA} ${branchA}`
                .toUpperCase()
                .replace(/\s+/g, " ")
                .trim();
              const bankAndBranchB = `${bankB} ${branchB}`
                .toUpperCase()
                .replace(/\s+/g, " ")
                .trim();
              const branchAndBankA = `${branchA} ${bankA}`
                .toUpperCase()
                .replace(/\s+/g, " ")
                .trim();
              const branchAndBankB = `${branchB} ${bankB}`
                .toUpperCase()
                .replace(/\s+/g, " ")
                .trim();

              const score = (
                bank: string,
                branch: string,
                ifsc: string,
                city: string,
                state: string,
                addr: string,
                bankAndBranch: string,
                branchAndBank: string,
              ) => {
                // 1. Exact IFSC code matching
                if (ifsc === q) return 10000000;

                // 2. Exact Bank + Branch matching either orientation
                if (bankAndBranch === q || branchAndBank === q) return 5000000;

                // 3. Exact Bank Name match
                if (bank === q) return 2000000;

                // 4. Exact Branch Name match
                if (branch === q) return 1000000;

                // 5. Starts With Check
                if (
                  ifsc.startsWith(q) ||
                  bank.startsWith(q) ||
                  branch.startsWith(q) ||
                  city.startsWith(q) ||
                  state.startsWith(q)
                ) {
                  let boost = 0;
                  if (ifsc.startsWith(q)) boost += 50000;
                  if (bank.startsWith(q)) boost += 40000;
                  if (branch.startsWith(q)) boost += 30000;
                  if (city.startsWith(q)) boost += 20000;
                  if (state.startsWith(q)) boost += 10000;
                  return 100000 + boost;
                }

                // 6. Contains Check
                const combined =
                  `${bank} ${branch} ${ifsc} ${city} ${state} ${addr}`.toUpperCase();
                if (combined.includes(q)) {
                  let boost = 0;
                  if (ifsc.includes(q)) boost += 500;
                  if (bank.includes(q)) boost += 400;
                  if (branch.includes(q)) boost += 300;
                  if (city.includes(q)) boost += 200;
                  if (state.includes(q)) boost += 100;
                  return 1000 + boost;
                }

                return 0;
              };

              return (
                score(
                  bankB,
                  branchB,
                  ifscB,
                  cityB,
                  stateB,
                  addrB,
                  bankAndBranchB,
                  branchAndBankB,
                ) -
                score(
                  bankA,
                  branchA,
                  ifscA,
                  cityA,
                  stateA,
                  addrA,
                  bankAndBranchA,
                  branchAndBankA,
                )
              );
            });
          }

          dbCache.searchBranches[cacheKey] = {
            data: result,
            timestamp: now,
          };
          return result;
        }
        console.warn(
          "Full-text search error, falling back to basic matching:",
          error,
        );
      } catch (e) {
        console.error("Supabase Search client failure:", e);
      }
    }
    return [];
  },

  async logAdminAudit(
    action: string,
    resourceType: string | null = null,
    resourceId: string | null = null,
    email?: string,
  ): Promise<void> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const adminId = session?.user?.id || null;
        await supabase.from("admin_audit_logs").insert({
          admin_id: adminId,
          action: email ? `${action} (Login user: ${email})` : action,
          resource_type: resourceType,
          resource_id: resourceId,
        });
      } catch (e) {
        console.error("Failed to write admin audit log:", e);
      }
    }
  },

  async importExcelData(
    filename: string,
    bankName: string,
    branches: Omit<BankBranch, "id" | "bank_id">[],
    fileHash: string,
  ): Promise<{
    totalRows: number;
    newRecords: number;
    updatedRecords: number;
    failedRecords: number;
  }> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const cleanBankName = bankName.trim().toUpperCase();
        const bankSlug = cleanBankName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");

        let { data: bankData } = await supabase
          .from("banks")
          .select("id, name, slug, logo_url")
          .eq("slug", bankSlug)
          .maybeSingle();

        if (!bankData) {
          const { data: newBank, error: insertBankErr } = await supabase
            .from("banks")
            .insert({
              name: cleanBankName,
              slug: bankSlug,
              logo_url: null,
            })
            .select()
            .single();

          if (insertBankErr || !newBank) {
            throw new Error(
              `Failed to insert target bank: ${insertBankErr?.message}`,
            );
          }
          bankData = newBank;
        }

        if (!bankData) {
          throw new Error("Bank record could not be resolved.");
        }

        const recordsToInsert = branches.map((br) => ({
          bank_id: bankData.id,
          ifsc: br.ifsc.toUpperCase(),
          branch_name: br.branch_name.toUpperCase(),
          address: br.address.toUpperCase(),
          city1: br.city1.toUpperCase(),
          city2: br.city2 ? br.city2.toUpperCase() : null,
          display_city: br.display_city.toUpperCase(),
          state: br.state.toUpperCase(),
          std_code: br.std_code || null,
          phone: br.phone || null,
          slug: br.slug,
          search_text: br.search_text || null,
        }));

        // 1. Determine which IFSCs are already present in the database to accurately separate INSERTs vs UPDATEs
        const ifscList = recordsToInsert.map((r) => r.ifsc);
        const existingIfscs = new Set<string>();

        // Query database in chunks of 500 to avoid query size limits
        const queryBatchSize = 500;
        for (let i = 0; i < ifscList.length; i += queryBatchSize) {
          const chunk = ifscList.slice(i, i + queryBatchSize);
          const { data, error } = await supabase
            .from("bank_branches")
            .select("ifsc")
            .in("ifsc", chunk);

          if (!error && data) {
            data.forEach((row) => {
              if (row.ifsc) {
                existingIfscs.add(row.ifsc.toUpperCase());
              }
            });
          }
        }

        let succeededNew = 0;
        let succeededUpdated = 0;
        let failed = 0;

        // 2. Insert or update branches using upsert in onConflict: 'ifsc' mode
        const batchSize = 100;
        for (let i = 0; i < recordsToInsert.length; i += batchSize) {
          const batch = recordsToInsert.slice(i, i + batchSize);
          const { error: upsertErr } = await supabase
            .from("bank_branches")
            .upsert(batch, { onConflict: "ifsc" });

          if (upsertErr) {
            failed += batch.length;
            console.error("Batch branch load error or violation:", upsertErr);
          } else {
            // Classify each successful record as an INSERT (new) or UPDATE (updated)
            batch.forEach((rec) => {
              if (existingIfscs.has(rec.ifsc)) {
                succeededUpdated++;
              } else {
                succeededNew++;
              }
            });
          }
        }

        // 3. Record import historic log
        await supabase.from("import_logs").insert({
          filename,
          bank_name: cleanBankName,
          records_imported: succeededNew + succeededUpdated,
          records_failed: failed,
          new_records: succeededNew,
          updated_records: succeededUpdated,
          file_hash: fileHash,
        });

        // 4. Save to admin audit trail
        await this.logAdminAudit(
          `IFSC Sync Workbook Imported: ${filename} for ${cleanBankName}. Succeeded: ${succeededNew + succeededUpdated} (New: ${succeededNew}, Updated: ${succeededUpdated}), Failed: ${failed}`,
          "import_logs",
          fileHash,
        );

        return {
          totalRows: branches.length,
          newRecords: succeededNew,
          updatedRecords: succeededUpdated,
          failedRecords: failed,
        };
      } catch (err: any) {
        console.error("Workbook sync import process failure:", err);
        throw err;
      }
    }
    throw new Error(
      "Supabase Client is not configured. Please supply VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.",
    );
  },

  async getCityBranchCount(cityName: string): Promise<number> {
    const key = cityName.trim().toUpperCase();
    if (dbCache.cityBranchCounts[key] !== undefined) {
      return dbCache.cityBranchCounts[key];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const city = cityName.toUpperCase();
        const { count, error } = await supabase
          .from("bank_branches")
          .select("id", { count: "exact", head: true })
          .eq("display_city", city);
        if (!error && count !== null) {
          dbCache.cityBranchCounts[key] = count;
          return count;
        }
      } catch (e) {
        console.error("Error fetching city branch count:", e);
      }
    }
    return 0;
  },

  async getBranchesByCity(
    cityName: string,
    page = 1,
    pageSize = 15,
  ): Promise<{ data: (BankBranch & { bankName: string })[]; count: number }> {
    const cityKey = cityName.trim().toUpperCase();
    const cacheKey = `${cityKey}_${page}_${pageSize}`;
    if (dbCache.branchesByCity[cacheKey] !== undefined) {
      return dbCache.branchesByCity[cacheKey];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const city = cityName.toUpperCase();
        const fromIdx = (page - 1) * pageSize;
        const toIdx = fromIdx + pageSize - 1;

        const cachedCount = dbCache.cityBranchCounts[cityKey];

        const { data, count, error } = await supabase
          .from("bank_branches")
          .select(
            "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, phone, std_code, slug, banks(id, name, slug, logo_url)",
            cachedCount !== undefined ? {} : { count: "exact" },
          )
          .eq("display_city", city)
          .order("branch_name", { ascending: true })
          .range(fromIdx, toIdx);

        if (!error && data) {
          const list = (data as any[]).map((br) => ({
            ...br,
            bankName: br.banks ? br.banks.name : "Unknown Bank",
          }));
          const finalCount = cachedCount !== undefined ? cachedCount : count || 0;
          if (cachedCount === undefined && count !== null) {
            dbCache.cityBranchCounts[cityKey] = count;
          }
          const result = { data: list, count: finalCount };
          dbCache.branchesByCity[cacheKey] = result;
          return result;
        }
      } catch (e) {
        console.error("Error getting branches by city:", e);
      }
    }
    return { data: [], count: 0 };
  },

  async getBanksInCity(cityName: string): Promise<Bank[]> {
    const key = cityName.trim().toUpperCase();
    if (dbCache.banksInCity[key] !== undefined) {
      return dbCache.banksInCity[key];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const city = cityName.toUpperCase();
        const { data, error } = await supabase
          .from("bank_branches")
          .select("bank_id, banks(id, name, slug, logo_url)")
          .eq("display_city", city)
          .limit(1000);

        if (!error && data) {
          const uniqueBanksMap = new Map<string, Bank>();
          data.forEach((item: any) => {
            if (item.banks) {
              uniqueBanksMap.set(item.banks.id, item.banks);
            }
          });
          const result = Array.from(uniqueBanksMap.values()).sort((a, b) =>
            a.name.localeCompare(b.name),
          );
          dbCache.banksInCity[key] = result;
          return result;
        }
      } catch (e) {
        console.error("Error getting banks in city:", e);
      }
    }
    return [];
  },

  async getRecentBranchesInCity(
    cityName: string,
    limit = 5,
  ): Promise<(BankBranch & { bankName: string })[]> {
    const key = `${cityName.trim().toUpperCase()}_${limit}`;
    if (dbCache.recentBranchesInCity[key] !== undefined) {
      return dbCache.recentBranchesInCity[key];
    }

    if (isSupabaseConfigured() && supabase) {
      try {
        const city = cityName.toUpperCase();
        const { data, error } = await supabase
          .from("bank_branches")
          .select(
            "id, bank_id, ifsc, branch_name, address, city1, city2, display_city, state, phone, std_code, slug, banks(id, name, slug, logo_url)",
          )
          .eq("display_city", city)
          .order("created_at", { ascending: false })
          .limit(limit);

        if (!error && data) {
          const result = (data as any[]).map((br) => ({
            ...br,
            bankName: br.banks ? br.banks.name : "Unknown Bank",
          }));
          dbCache.recentBranchesInCity[key] = result;
          return result;
        }
      } catch (e) {
        console.error("Error getting recent branches in city:", e);
      }
    }
    return [];
  },

  async getAffiliateCampaigns(): Promise<AffiliateCampaign[]> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("affiliate_campaigns")
          .select("*")
          .order("display_order", { ascending: true });
        if (!error && data) {
          return data as AffiliateCampaign[];
        }
      } catch (e) {
        console.warn(
          "Real affiliate_campaigns table not configured, falling back:",
          e,
        );
      }
    }
    return [];
  },

  async createAffiliateCampaign(campaign: Omit<AffiliateCampaign, "id">): Promise<AffiliateCampaign | null> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("affiliate_campaigns")
          .insert([campaign])
          .select()
          .single();
        if (!error && data) {
          return data as AffiliateCampaign;
        } else if (error) {
          console.error("Error creating campaign:", error);
        }
      } catch (e) {
        console.error("Exception creating campaign:", e);
      }
    }
    return null;
  },

  async updateAffiliateCampaign(id: string, campaign: Partial<AffiliateCampaign>): Promise<AffiliateCampaign | null> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("affiliate_campaigns")
          .update(campaign)
          .eq("id", id)
          .select()
          .single();
        if (!error && data) {
          return data as AffiliateCampaign;
        } else if (error) {
          console.error("Error updating campaign:", error);
        }
      } catch (e) {
        console.error("Exception updating campaign:", e);
      }
    }
    return null;
  },

  async deleteAffiliateCampaign(id: string): Promise<boolean> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const { error } = await supabase
          .from("affiliate_campaigns")
          .delete()
          .eq("id", id);
        if (!error) {
          return true;
        } else {
          console.error("Error deleting campaign:", error);
        }
      } catch (e) {
        console.error("Exception deleting campaign:", e);
      }
    }
    return false;
  },

  async getRevenueSettings(): Promise<RevenueSetting[]> {
    if (isSupabaseConfigured() && supabase) {
      try {
        const { data, error } = await supabase
          .from("revenue_settings")
          .select("*");
        if (!error && data) {
          return data as RevenueSetting[];
        }
      } catch (e) {
        console.warn("Error getting revenue settings, table might be unprovisioned:", e);
      }
    }
    return [];
  },

  async updateRevenueSetting(position: string, setting: Partial<RevenueSetting>): Promise<RevenueSetting | null> {
    if (isSupabaseConfigured() && supabase) {
      try {
        // Since we have a unique constraint on public.revenue_settings(position),
        // we can perform an upsert based on position.
        const { data, error } = await supabase
          .from("revenue_settings")
          .upsert({
            position,
            type: setting.type || "adsense_banner",
            enabled: setting.enabled !== undefined ? setting.enabled : true,
            slot_id: setting.slot_id || "",
            metadata: setting.metadata || {}
          }, { onConflict: "position" })
          .select()
          .single();
        if (!error && data) {
          return data as RevenueSetting;
        } else if (error) {
          console.error("Error upserting revenue setting:", error);
        }
      } catch (e) {
        console.error("Exception updating revenue setting:", e);
      }
    }
    return null;
  },
};